<template>
    <form class="flex flex-col pt-3 md:pt-8" :class="theme" @submit.prevent="handleSubmit">
        <div class="flex flex-col pt-4" data-cy="reset_password">
            <ui-label for="password">
                {{ $t('auth.form.fields.password') }}
            </ui-label>
            <ui-input
                v-model="v$.reset.password.$model"
                type="password"
                id="password"
                :class="{ 'form-error': v$.reset.password.$error }"
            />
            <div
                :class="{ 'text-default-red': v$.reset.password.$error }"
                data-cy="reset_password-error"
                v-if="v$.reset.password.required.$invalid && v$.reset.password.$error"
            >
                {{ $t('validation.required') }}
            </div>
            <div
                :class="{ 'text-default-red': v$.reset.password.$error }"
                data-cy="reset_password-min-length-error"
                v-if="v$.reset.password.minLength.$invalid && v$.reset.password.$error"
            >
                {{ $t('validation.min.string', { attribute: 'Password', min: config.password_minimum_length }) }}
            </div>
            <div
                data-cy="reset_password-numbers-error"
                :class="{ 'text-default-red': v$.reset.password.$error }"
                v-if="v$.reset.password.includeNumber.$invalid && v$.reset.password.$error"
            >
                {{ $t('validation.include_numbers', { attribute: 'Password' }) }}
            </div>
            <div
                data-cy="reset_password-symbols-error"
                :class="{ 'text-default-red': v$.reset.password.$error }"
                v-if="v$.reset.password.includeSymbol.$invalid && v$.reset.password.$error"
            >
                {{ $t('validation.include_symbols', { attribute: 'Password' }) }}
            </div>
            <validation-errors :errors="validation.errors.password" v-if="validation.errors" />
        </div>

        <div class="flex flex-col pt-4" data-cy="reset_confirm_password">
            <ui-label for="confirm_password">
                {{ $t('auth.form.fields.confirm_password') }}
            </ui-label>
            <ui-input
                v-model="v$.reset.confirm_password.$model"
                type="password"
                id="confirm_password"
                :class="{ 'form-error': v$.reset.confirm_password.$error }"
            />
            <div
                :class="{ 'text-default-red': v$.reset.confirm_password.$error }"
                data-cy="reset_confirm_password-error"
                v-if="v$.reset.confirm_password.required.$invalid && v$.reset.confirm_password.$error"
            >
                {{ $t('validation.required') }}
            </div>
            <div
                data-cy="reset_confirm_password-match-error"
                :class="{ 'text-default-red': v$.reset.confirm_password.$error }"
                v-if="
                    !v$.reset.confirm_password.required.$invalid &&
                    v$.reset.confirm_password.sameAsPassword.$invalid &&
                    v$.reset.confirm_password.$error
                "
            >
                {{ $t('validation.same', { attribute: 'Password', other: 'Confirm Password' }) }}
            </div>
            <validation-errors :errors="validation.errors.confirm_password" v-if="validation.errors" />
        </div>

        <div class="text-gray-600 text-xs italic mb-6">
            {{ $t('auth.reset.criteria.heading') }}
            <ul class="list-disc px-4">
                <li v-if="config.password_minimum_length > 0">
                    {{ $t('auth.reset.criteria.min_length', { attribute: config.password_minimum_length }) }}
                </li>
                <li v-if="config.password_include_numbers">
                    {{ $t('auth.reset.criteria.include_numbers') }}
                </li>
                <li v-if="config.password_include_symbols">
                    {{ $t('auth.reset.criteria.include_symbols') }}
                </li>
            </ul>
        </div>

        <p class="text-default-red text-center pt-4" data-cy="form-error-messages" v-if="validation.message">
            {{ validation.message }}
        </p>

        <ui-submit-button data-cy="reset_button" class="auth-button" :disabled="processing">
            <span v-if="!processing">
                {{ $t('auth.form.buttons.reset') }}
            </span>
            <span v-else>
                {{ $t('auth.form.buttons.resetting') }}
            </span>
        </ui-submit-button>
    </form>
</template>

<script>
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required, sameAs } from '@vuelidate/validators';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiInput,
        UiLabel,
        UiSubmitButton,
        ValidationErrors,
    },
    data() {
        return {
            config: {},
        };
    },
    mounted() {
        if (this.token) {
            this.$http.all([this.$http.post('/api/password/get-config/' + this.token)]).then(
                this.$http.spread((config) => {
                    let config_data = {
                        ...config.data,
                        password_include_numbers: config.data.password_include_numbers ? true : false,
                        password_include_symbols: config.data.password_include_symbols ? true : false,
                    };

                    this.config = config_data;
                }),
            );
        }
        if (this.user_id) {
            this.$http
                .all([
                    this.$http.post('/api/users/get-config', {
                        id: this.user_id,
                    }),
                ])
                .then(
                    this.$http.spread((config) => {
                        let config_data = {
                            ...config.data,
                            password_include_numbers: config.data.password_include_numbers ? true : false,
                            password_include_symbols: config.data.password_include_symbols ? true : false,
                        };

                        this.config = config_data;
                    }),
                );
        }
    },
    validations: {
        reset: {
            password: {
                required,
                minLength(value) {
                    if (this.config.password_minimum_length > 0) {
                        return value.length >= this.config.password_minimum_length;
                    }

                    return true;
                },
                includeNumber(value) {
                    if (this.config.password_include_numbers) {
                        return /[0-9]/.test(value);
                    }

                    return true;
                },
                includeSymbol(value) {
                    if (this.config.password_include_symbols) {
                        return /[#?!@$%^&*-]/.test(value);
                    }

                    return true;
                },
            },
            confirm_password: {
                required,
                sameAsPassword(value) {
                    return value === this.reset.password;
                },
            },
        },
    },
    props: ['reset', 'validation', 'processing', 'user_id', 'token', 'theme'],
    methods: {
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            this.$emit('formSubmit');
        },
    },
};
</script>
